import { accessScopes, policyMatrix, publicReadGrants } from '../../config/permissions-config.ts';
import type { EntityActionType } from '../../types.ts';
import type { AccessScope, AccessScopedEntityType } from './access-scopes.ts';
import { getAllDecisions } from './engine/check.ts';
import { type EngineAccess, getDecisionsForAccesses } from './engine/resolve-access.ts';
import { mayGrantRole, type RoleGrant } from './engine/role-grant.ts';
import type { AccessMembership, PermissionCheckOptions, SubjectForPermission } from './engine/types.ts';

/**
 * A signed-in actor as the checks read it. `actorId` is a user or service account id. `scopes` is the mask of the API
 * key or access token that proved the caller, required so a hand-built actor states its mask (null = unmasked, what a
 * session passes) and can never fail open by omission.
 */
type ActorFields = { actorId: string; isSystemAdmin?: boolean; scopes: readonly AccessScope[] | null };

/**
 * Memberships and actor travel together, so no call site can pair one user's memberships with
 * another's actor. An anonymous access carries no memberships.
 */
export type Access<T extends AccessMembership = AccessMembership> = (ActorFields & { memberships: T[] }) | { anonymous: true };

/**
 * The scope mask: a scoped API key or access token (one with `scopes`) may do less than its actor's grants allow,
 * never more. Unscoped access (a session, or `scopes: null`) passes untouched.
 */
const scopeAllows = (access: Access, entityType: AccessScopedEntityType, action: EntityActionType): boolean =>
  'anonymous' in access || accessScopes.allows(access.scopes, entityType, action);

/** System admins bypass every check; anonymous actors hold nothing. */
const toEngineAccess = <T extends AccessMembership>(access: Access<T>): EngineAccess<T> =>
  'anonymous' in access
    ? { memberships: [] }
    : { memberships: access.memberships, actorId: access.actorId, isSystemAdmin: access.isSystemAdmin === true };

export interface PermissionResult<T extends AccessMembership = AccessMembership> {
  allowed: boolean;
  /** The decision's `membership` (`PermissionDecision`). */
  membership: T | null;
}

// Every entry point injects the same public grants and reads elevation off the app's hierarchy. SQL collection
// predicates are the tested database-side projection of the same decisions.
const accessOptions = <T extends AccessMembership>(engineAccess: EngineAccess<T>): PermissionCheckOptions => ({
  publicGrants: publicReadGrants,
  actorId: engineAccess.actorId,
  isSystemAdmin: engineAccess.isSystemAdmin,
});

/** The request-path check. @see cella/PERMISSIONS.md */
export const checkAccess = <T extends AccessMembership>(
  access: Access<T>,
  action: EntityActionType,
  subject: SubjectForPermission,
): PermissionResult<T> => {
  const engineAccess = toEngineAccess(access);
  const { can, membership } = getAllDecisions(policyMatrix, engineAccess.memberships, subject, accessOptions(engineAccess));
  return { allowed: can[action] && scopeAllows(access, subject.entityType, action), membership };
};

/** Whether the actor may hand out a role on a channel under the configured policies: `mayGrantRole`'s rule. */
export const checkRoleGrant = <T extends AccessMembership>(access: Access<T>, grant: RoleGrant): boolean => {
  const engineAccess = toEngineAccess(access);
  const options = accessOptions(engineAccess);
  const allows = (memberships: T[]) => getAllDecisions(policyMatrix, memberships, grant.subject, options).can[grant.action];
  return mayGrantRole(engineAccess.memberships, grant, allows, options);
};

/**
 * One actor, many rows (bulk splits and list reads), in one engine pass.
 * @returns Keyed by subject id, or `_idx:<position>` for a subject without one.
 */
export const checkAccessBatch = <T extends AccessMembership>(
  access: Access<T>,
  action: EntityActionType,
  subjects: SubjectForPermission[],
): Map<string, PermissionResult<T>> => {
  const engineAccess = toEngineAccess(access);
  const decisions = getAllDecisions(policyMatrix, engineAccess.memberships, subjects, accessOptions(engineAccess));
  const results = new Map<string, PermissionResult<T>>();
  subjects.forEach((subject, i) => {
    const key = subject.id ?? `_idx:${i}`;
    const decision = decisions.get(key);
    if (decision)
      results.set(key, { allowed: decision.can[action] && scopeAllows(access, subject.entityType, action), membership: decision.membership });
  });
  return results;
};

/**
 * Many actors, one row: stream fan-out. An access whose memberships the engine cannot read is denied alone.
 * @returns Whether each access may take the action, in their order. @see resolve-access.ts
 */
export const checkAccessFanout = (accesses: Access[], action: EntityActionType, subject: SubjectForPermission): boolean[] => {
  const decisions = getDecisionsForAccesses(policyMatrix, accesses.map(toEngineAccess), subject, { publicGrants: publicReadGrants });
  return decisions.map((can, i) => {
    const access = accesses[i];
    return can[action] && access !== undefined && scopeAllows(access, subject.entityType, action);
  });
};
